import React from 'react';
import { Box, Grid, Paper, Typography,Chip, Divider, Table, TableBody, TableRow, TableCell, TableContainer, Link } from '@material-ui/core';

import { Title } from 'app/components';
import ConnectionsTable from '../ConnectionsTable';
import KeywordChip from '../KeywordChip';

const isHeading = (p) => {
  if (!p) return false;
  const clean = p.trim();
  if (!clean || clean.length > 120) return false;
  if (clean === clean.toUpperCase()) return true;
  return clean.split(' ').some((w) => w.length > 3 && w === w.toUpperCase());
};


function OrganisationMatchDetail({ matchRawResult, classes }) {
  if (!matchRawResult) return null;
  
  const filteredMedia = (matchRawResult?.media || []).filter((item) => {
    const title = (item.title || "").toLowerCase();
    const link = (item.externalLink || "").toLowerCase();

    return !title.includes("factiva") && !link.includes("factiva");
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <Paper className={classes.table} style={{ overflow: 'hidden' }}>
            <Title prefix="Key Information" className={classes.title} />

            {matchRawResult && (
              <>
                {/* ── Header: avatar + name + badge ── */}
                <Box display="flex" alignItems="center" style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.08)', gap: 12 }}>
                  <Box
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <Typography style={{ fontWeight: 600, fontSize: 15, color: '#1976d2' }}>
                      {matchRawResult?.lists?.businessName
                        ?.split(/[\s,]+/)
                        .filter(Boolean)
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography style={{ fontWeight: 600, fontSize: 15 }}>{matchRawResult?.lists?.businessName}</Typography>
                    <Typography style={{ fontSize: 13, opacity: 0.6 }}>{matchRawResult?.peps?.natureOfBusiness?.join(', ')}</Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
                    {matchRawResult?.aml_types?.map((aml, i) => (
                      <KeywordChip key={i} code={aml} classes={classes} />
                    ))}
                  </Box>
                </Box>

                {/* ── Business Information ── */}
                <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Business Information</Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                    {[
                      { label: 'Business name', value: matchRawResult?.lists?.businessName },
                      { label: 'Country of incorporation', value: matchRawResult?.lists?.countryOfIncorporation },
                      {
                        label: 'Date of incorporation',
                        value: matchRawResult?.peps?.dayOfIncorporation ? `${matchRawResult.peps.dayOfIncorporation}-${matchRawResult.peps.monthOfIncorporation}-${matchRawResult.peps.yearOfIncorporation}` : null,
                      },
                      { label: 'Nature of business', value: matchRawResult?.peps?.natureOfBusiness?.join(', ') },
                    ].map(({ label, value }) => (
                      <Box key={label} style={{ minWidth: 140 }}>
                        <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{label}</Typography>
                        <Typography style={{ fontSize: 13, opacity: value ? 1 : 0.35 }}>{value || '—'}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* ── Aliases ── */}
                {matchRawResult?.detail?.profile?.person?.aliases?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        opacity: 0.45,
                        textTransform: 'uppercase',
                        marginBottom: 10,
                      }}
                    >
                      Aliases
                    </Typography>

                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {matchRawResult.detail.profile.person.aliases.map((alias, idx) => (
                        <Chip
                          key={idx}
                          label={alias}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {/* ── Addresses ── */}
                {matchRawResult?.detail?.profile?.person?.addresses?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        opacity: 0.45,
                        textTransform: 'uppercase',
                        marginBottom: 10,
                      }}
                    >
                      Addresses
                    </Typography>

                    {matchRawResult.detail.profile.person.addresses.map((a, idx) => (
                      <Paper
                        key={idx}
                        variant="outlined"
                        style={{
                          padding: 12,
                          marginBottom: 8,
                          borderRadius: 8,
                        }}
                      >
                        <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                          {a.address}
                        </Typography>

                        <Typography style={{ fontSize: 12, opacity: 0.7 }}>
                          {a.city}, {a.postalCode}
                        </Typography>

                        <Typography style={{ fontSize: 12, opacity: 0.7 }}>
                          {a.country}
                        </Typography>
                        {a.url && (
                          <Link
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12 }}
                          >
                            {a.url}
                          </Link>
                        )}
                      </Paper>
                    ))}
                  </Box>
)}

                {/* ── Identification Details ── */}
                {matchRawResult?.peps?.identificationDetails?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Identification</Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                      {matchRawResult.peps.identificationDetails.map((id, i) => (
                        <Box key={i}>
                          <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{id.type}</Typography>
                          <Typography style={{ fontSize: 13, fontWeight: 600 }}>{id.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* ── Risk Classifications ── */}
                {matchRawResult?.lists?.referenceList?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Risk Classifications</Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                      {matchRawResult.lists.referenceList.map((ref, i) => {
                        const listItems = [
                          ...(ref.sanctions || []).map((s) => ({ ...s, type: 'sanctions' })),
                          ...(ref.otherOfficialList || []).map((o) => ({ ...o, type: 'ool' })),
                          ...(ref.specialInterestPersonList || []).map((s) => ({ ...s, type: 'si' })),
                        ];
                        return (
                          <Box
                            key={i}
                            style={{
                              background: 'rgba(0,0,0,0.02)',
                              border: '0.5px solid rgba(0,0,0,0.08)',
                              borderRadius: 6,
                              padding: '10px 12px',
                            }}>
                            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: listItems.length > 0 ? 8 : 0 }}>
                              <KeywordChip code={ref.keyword} classes={classes} />
                              <Typography style={{ fontSize: 13, flex: 1 }}>{ref.category}</Typography>
                              <Box
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  background: ref.status === 'ACTIVE' ? 'rgba(76,175,80,0.1)' : 'rgba(0,0,0,0.06)',
                                  color: ref.status === 'ACTIVE' ? '#2e7d32' : '#666',
                                }}>
                                {ref.status}
                              </Box>
                            </Box>

                            {ref.subCategory && <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>{ref.subCategory}</Typography>}

                            {listItems.length > 0 && (
                              <Box style={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {listItems.map((item, j) => (
                                  <Box key={j} display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                                    <Box style={{ width: 4, height: 4, borderRadius: '50%', background: '#999', marginTop: 6, flexShrink: 0 }} />
                                    <Box>
                                      <Typography style={{ fontSize: 12, fontWeight: 600 }}>{item.name}</Typography>
                                      {item.from?.year && (
                                        <Typography style={{ fontSize: 11, opacity: 0.5 }}>
                                          Since {[item.from.day, item.from.month, item.from.year].filter(Boolean).join('/')}
                                          {item.to ? ` – ${[item.to.day, item.to.month, item.to.year].filter(Boolean).join('/')}` : ' – present'}
                                        </Typography>
                                      )}
                                      {item.sources?.length > 0 && (
                                        <Box mt={0.5}>
                                          {item.sources.map((src, k) => {
                                            const isString = typeof src === 'string';
                                            const url = isString ? src : src.sourceName;
                                            const label = isString ? src : [src.sourceName, src.sourceDate, src.sourceLanguage].filter(Boolean).join(' · ');
                                            return url ? (
                                              <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, display: 'block', wordBreak: 'break-all', color: '#1976d2' }}>
                                                {label}
                                              </a>
                                            ) : (
                                              <span key={k} style={{ fontSize: 11, display: 'block', color: '#666' }}>
                                                {label}
                                              </span>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* ── CONNECTIONS / ASSOCIATES ── */}
        {(matchRawResult?.lists?.connections?.length > 0 || matchRawResult?.lists?.linkedIndividuals?.length > 0 || matchRawResult?.lists?.linkedBusinesses?.length > 0) && (
          <Grid item md={6} xs={12}>
            <Paper className={classes.table} style={{ overflow: 'hidden' }}>
              <Title prefix="Connections & Associates" className={classes.title} />
              <Grid container spacing={2} style={{ padding: '14px 16px' }}>
                {matchRawResult?.lists?.connections?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Connections" rows={matchRawResult.lists.connections} classes={classes} />
                  </Grid>
                )}
                {matchRawResult?.lists?.linkedIndividuals?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Linked Individuals" rows={matchRawResult.lists.linkedIndividuals} classes={classes} />
                  </Grid>
                )}
                {matchRawResult?.lists?.linkedBusinesses?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Linked Businesses" rows={matchRawResult.lists.linkedBusinesses} classes={classes} />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* ── MEDIA ── */}
      {filteredMedia.length > 0 && (
        <Grid item xs={12}>
          <TableContainer component={Paper} className={classes.table}>
            <Title prefix="Media" className={classes.title} />
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Grid container spacing={2}>
                      {filteredMedia.map((item, i) => (
                        <Grid item xs={12} md={6} key={i}>
                          <Box
                            style={{
                              border: "0.5px solid rgba(0,0,0,0.1)",
                              borderRadius: 6,
                              padding: 10,
                            }}
                          >
                            <Link
                              href={item.externalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.title || item.externalLink}
                            </Link>

                            {item.title && (
                              <Typography
                                variant="caption"
                                display="block"
                                style={{ color: "gray", wordBreak: "break-all" }}
                              >
                                {item.externalLink}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {/* ── PROFILE NOTES ── */}
      {matchRawResult?.detail?.profile?.person?.profileNotes?.length > 0 && (
        <Grid item xs={12}>
          <Paper className={classes.table}>
            <Box style={{ padding: 16 }}>
              {/* TITLE */}
              <Title prefix="Profile Notes" className={classes.title} />

              {/* NOTES */}
              {matchRawResult.peps.profileNotes.map((note, i) => {
                const paragraphs = note
                  .split('\n')
                  .map((p) => p.trim())
                  .filter(Boolean);

                return (
                  <Box key={i} mb={4}>
                    {/* NOTE HEADER */}
                    <Typography
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: '#9c27b0',
                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                        paddingBottom: 6,
                      }}>
                      Note {i + 1}
                    </Typography>

                    {/* CONTENT */}
                    {paragraphs.map((p, idx) => {
                      const heading = isHeading(p);

                      return (
                        <Box key={idx} mb={1.5}>
                          {/* Divider between sections */}
                          {heading && idx !== 0 && <Divider style={{ margin: '12px 0' }} />}

                          <Typography
                            style={{
                              fontWeight: heading ? 600 : 400,
                              fontSize: heading ? 13 : 14,
                              color: heading ? '#1976d2' : '#333',
                              textTransform: heading ? 'uppercase' : 'none',
                              letterSpacing: heading ? '0.04em' : 'normal',
                              lineHeight: 1.6,
                            }}>
                            {p}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      )}
    </>
  );
}

export default OrganisationMatchDetail;
